/**
 * CRA3D Voice Answering — reusable "Speak answer" button.
 *
 * Placement rule (Mark's spec): attach to FREE-TEXT fields only —
 * assessment free-text, work-history/story explanations, evidence
 * corrections, portfolio/capstone text fields, Resume Studio section
 * editing. NOT on radio / checkbox / scale / slider.
 *
 * Behavior rule: never overwrite the existing text. The transcript is
 * APPENDED (with an intelligent separator: newline if the field is
 * non-empty, else nothing). The parent decides how to merge via the
 * `onTranscript(text, meta)` callback — for a plain textarea this is
 * "append", for a structured field the parent can transform first.
 *
 * Privacy rule: mic is NEVER pre-requested. The permission prompt
 * fires only on the customer's first Tap. Recording is opt-in every
 * time. No continuous listening.
 *
 * Fallback rule: unsupported browser hides the button entirely. When
 * the backend reports `provider_configured: false`, we render the
 * button as disabled with the honest "Voice input isn't available
 * right now — please type your answer." tooltip.
 *
 * Accessibility: the recording state is announced via aria-live.
 * Button is keyboard-operable (Enter / Space) via the underlying
 * <button>. Focus ring is preserved from shadcn Button.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Mic, Square, X, Loader2, AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";

// Token key MUST match `/app/frontend/src/lib/api.js` — that module is
// the single source of truth for the auth token; the mic button uses
// axios directly (multipart uploads) so we read the same key.
const TOKEN_KEY = "cra3d.access_token";

const MAX_DEFAULT_SECONDS = 180;   // matches server hard cap (fetched from /status)
const RECORDER_MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",   // Safari iOS 14+
  "audio/ogg;codecs=opus",
];

function detectMediaSupport() {
  if (typeof window === "undefined") return false;
  if (!window.MediaRecorder) return false;
  if (!navigator?.mediaDevices?.getUserMedia) return false;
  return true;
}

function pickMime() {
  for (const m of RECORDER_MIME_CANDIDATES) {
    try {
      if (window.MediaRecorder.isTypeSupported(m)) return m;
    } catch (_) { /* ignore */ }
  }
  return "";
}

/**
 * Public API:
 *
 * <VoiceInputButton
 *   fieldTestid="…-voice-btn"           unique test id for this instance
 *   ariaLabel="Dictate this answer"     optional; defaults to "Speak answer"
 *   disabled={false}                    optional
 *   onTranscript={(text, meta) => …}    REQUIRED. Called with the plain
 *                                       transcript string plus metadata:
 *                                       { low_confidence, low_confidence_reason,
 *                                         duration_s, cost_usd, latency_ms }.
 *                                       Parent handles append into the
 *                                       existing field value.
 * />
 */
export function VoiceInputButton({
  fieldTestid,
  ariaLabel = "Speak answer",
  disabled = false,
  onTranscript,
}) {
  // --- capability probe (once)
  const [supported, setSupported] = useState(true);
  useEffect(() => { setSupported(detectMediaSupport()); }, []);

  // --- voice-status snapshot (one request per mount)
  const [status, setStatus] = useState(null);
  const [statusErr, setStatusErr] = useState(null);
  const loadStatus = useCallback(async () => {
    setStatusErr(null);
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setStatus(null); return; }
    const base = process.env.REACT_APP_BACKEND_URL;
    try {
      const { data } = await axios.get(`${base}/api/voice/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(data);
    } catch (err) {
      setStatusErr(err?.response?.data?.detail || "Voice status unavailable.");
    }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  // --- consent dialog + recorder state machine
  const [consentOpen, setConsentOpen] = useState(false);
  const [consenting, setConsenting] = useState(false);
  const [phase, setPhase] = useState("idle");
  // phase: idle | requesting_perm | recording | stopping | uploading | error | rate_limited
  const [errorMsg, setErrorMsg] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  // V1 (fix cycle 2026-09-25, Mark): honest countdown on 429. Reads
  // Retry-After (seconds) on the response and counts down to zero,
  // auto-re-enabling the button. Typing / saving / navigation stay
  // fully usable while this is running.
  const [cooldownSecs, setCooldownSecs] = useState(0);
  const cooldownTimerRef = useRef(null);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);

  const maxSeconds = status?.max_duration_seconds || MAX_DEFAULT_SECONDS;

  // --- shared cleanup (mic release + timer + refs)
  const teardown = useCallback(() => {
    try {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    } catch (_) { /* ignore */ }
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    } catch (_) { /* ignore */ }
    chunksRef.current = [];
    recorderRef.current = null;
  }, []);
  useEffect(() => () => teardown(), [teardown]);

  // --- Upload the recorded blob to /api/voice/transcribe. On success,
  //     the parent receives the transcript via `onTranscript(text, meta)`.
  const upload = useCallback(async (blob) => {
    setPhase("uploading");
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setPhase("error");
      setErrorMsg("Please sign in again to use voice input.");
      return;
    }
    const base = process.env.REACT_APP_BACKEND_URL;
    const form = new FormData();
    form.append("audio", blob, `voice-${Date.now()}.webm`);
    try {
      const { data } = await axios.post(`${base}/api/voice/transcribe`, form, {
        headers: { Authorization: `Bearer ${token}` },
        // Explicit content-type omitted so axios/browser sets the correct
        // multipart boundary. Do not force it here.
        timeout: 120000,
      });
      // Never overwrite. Never store the audio blob. Signal success upstream.
      onTranscript?.(data.transcript, {
        low_confidence:        data.low_confidence,
        low_confidence_reason: data.low_confidence_reason,
        duration_s:            data.duration_s,
        cost_usd:              data.cost_usd,
        latency_ms:            data.latency_ms,
        provider:              data.provider,
        model:                 data.model,
      });
      if (data.low_confidence) {
        toast.warning(
          "Please review — we may have misheard.",
          { description: "Your transcript was inserted so you can correct it." },
        );
      }
      setPhase("idle");
      setElapsed(0);
      // Refresh cap in the badge, if we're tracking it.
      loadStatus();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        // V1: read Retry-After (seconds by default in slowapi) and
        // start a countdown. Cap at a sane upper bound so a wildly
        // large value from a broken proxy never freezes the UI.
        const raHeader = err?.response?.headers?.["retry-after"]
                      ?? err?.response?.headers?.["Retry-After"];
        let secs = parseInt(raHeader, 10);
        if (!Number.isFinite(secs) || secs < 0) secs = 60;
        if (secs > 60 * 60) secs = 60 * 60;
        setCooldownSecs(secs);
        setPhase("rate_limited");
        setErrorMsg(null);
        return;
      }
      const msg = (detail && (detail.message || detail)) ||
                  "Voice transcription failed. Your typed text is preserved — please try again or continue typing.";
      setPhase("error");
      setErrorMsg(typeof msg === "string" ? msg : "Voice transcription failed.");
    }
  }, [onTranscript, loadStatus]);

  // V1: cooldown ticker + auto-re-enable at zero. Uses a single
  // setInterval per cooldown so we don't schedule multiple timers if
  // the user hits the mic again while paused.
  useEffect(() => {
    if (phase !== "rate_limited" || cooldownSecs <= 0) {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
      return;
    }
    if (cooldownTimerRef.current) return;
    cooldownTimerRef.current = setInterval(() => {
      setCooldownSecs((s) => {
        const next = s - 1;
        if (next <= 0) {
          if (cooldownTimerRef.current) {
            clearInterval(cooldownTimerRef.current);
            cooldownTimerRef.current = null;
          }
          setPhase("idle");
          // Refresh so the "N of 30 used" indicator updates.
          loadStatus();
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
    };
  }, [phase, cooldownSecs, loadStatus]);

  // --- Stop the recorder and (on non-cancel) fire the upload.
  const stop = useCallback((cancel = false) => {
    cancelledRef.current = cancel;
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      setPhase(cancel ? "idle" : "stopping");
      try {
        recorderRef.current.stop();
      } catch (_) { /* handler below still fires teardown */ }
    } else {
      teardown();
      setElapsed(0);
      setPhase("idle");
    }
  }, [teardown]);

  // --- Start recording (fires mic permission on first tap).
  const start = useCallback(async () => {
    setErrorMsg(null);
    setElapsed(0);
    setPhase("requesting_perm");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recorderRef.current = rec;
      chunksRef.current = [];
      cancelledRef.current = false;

      rec.ondataavailable = (e) => {
        if (e.data && e.data.size) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        const wasCancelled = cancelledRef.current;
        const type = rec.mimeType || "audio/webm";
        // Assemble the blob first, then release the mic.
        const blob = new Blob(chunksRef.current, { type });
        teardown();
        if (wasCancelled || blob.size === 0) {
          setElapsed(0);
          setPhase("idle");
          return;
        }
        await upload(blob);
      };

      rec.start(500); // 500 ms timeslices → decent bytes for a mid-cancel
      startedAtRef.current = Date.now();
      setPhase("recording");
      timerRef.current = setInterval(() => {
        const secs = Math.floor((Date.now() - startedAtRef.current) / 1000);
        setElapsed(secs);
        // Auto-stop at the hard cap.
        if (secs >= maxSeconds) {
          try { rec.stop(); } catch (_) { /* ignore */ }
        }
      }, 250);
    } catch (err) {
      teardown();
      setPhase("error");
      // Honest, actionable messages for the common failure modes.
      const name = err?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        setErrorMsg("Microphone permission was denied. You can still type your answer, or allow the mic in your browser settings.");
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        setErrorMsg("No microphone was detected. Please type your answer instead.");
      } else if (name === "NotReadableError") {
        setErrorMsg("The microphone is in use by another app. Close it and try again, or type your answer.");
      } else {
        setErrorMsg("We couldn't start recording. Please type your answer.");
      }
    }
  }, [teardown, upload, maxSeconds]);

  // --- Consent flow: on first tap, if consent missing, open modal.
  const handleTap = useCallback(async () => {
    if (disabled) return;
    if (!supported) return;
    // Provider not configured / voice disabled / offline → button was
    // disabled already; this branch is a defence-in-depth guard.
    if (!status?.provider_configured || !status?.voice_enabled) return;
    if (phase === "recording") { stop(false); return; }
    if (phase === "stopping" || phase === "uploading") return;
    if (!status?.consent) { setConsentOpen(true); return; }
    start();
  }, [disabled, supported, status, phase, stop, start]);

  const submitConsent = useCallback(async () => {
    setConsenting(true);
    const token = localStorage.getItem(TOKEN_KEY);
    const base = process.env.REACT_APP_BACKEND_URL;
    const v = status?.consent_policy?.version;
    if (!v) {
      // Guard: /voice/status hasn't returned a version yet, or the
      // provider is misconfigured. Refuse to POST rather than fall
      // back to a hardcoded string that could silently drift.
      toast.error("We couldn't read the current voice-consent version. Please refresh and try again.");
      setConsenting(false);
      return;
    }
    try {
      const form = new FormData();
      form.append("version", v);
      await axios.post(`${base}/api/voice/consent`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConsentOpen(false);
      await loadStatus();
      start();  // proceed straight into the mic-permission prompt
    } catch (err) {
      const msg = err?.response?.data?.detail?.message
                || err?.response?.data?.detail
                || "We couldn't record your consent. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Consent failed.");
    } finally {
      setConsenting(false);
    }
  }, [status, loadStatus, start]);

  // Nothing rendered if the browser doesn't support MediaRecorder /
  // getUserMedia. We still expose a small note via the parent's
  // fallback copy elsewhere ("Voice input isn't available in this
  // browser"). Rendering nothing keeps the UI clean and avoids a
  // disabled-forever button that would confuse the reader.
  if (!supported) return null;

  // Distinguish the reason voice is unavailable so the tooltip can
  // give an accurate cause. `provider_configured=false` is a service
  // configuration issue (EMERGENT_LLM_KEY missing on the server).
  // `voice_enabled=false` is an entitlement issue — voice isn't
  // included on the user's current plan. We treat these differently
  // in the UI copy (Phase 6, Mark): plan restriction gets a plan
  // message with no upgrade button (voice isn't a purchasable product),
  // service failure gets a retry hint and a support pointer.
  const providerMisconfigured = status && status.provider_configured === false;
  const planExcludesVoice     = status && status.provider_configured !== false
                              && status.voice_enabled === false;
  const providerBlocked = !!(providerMisconfigured || planExcludesVoice);
  const capReached =
    status && status.daily_remaining_minutes !== null
    && status.daily_remaining_minutes <= 0
    && status.daily_cap_minutes > 0;
  // V+ (fix cycle 2026-09-25, Mark): calm pre-limit indicator once
  // usage passes ~2/3 of the hourly cap. Sourced from /voice/status
  // (which reads llm_usage_logs, not the SlowAPI counter — so 429s
  // never make it here). Threshold is intentionally soft: shows up
  // only when useful, disappears when it doesn't help.
  const hourlyUsed = status?.hourly_transcribe_count;
  const hourlyCap  = status?.hourly_transcribe_cap;
  const showHourlyIndicator =
    Number.isFinite(hourlyUsed) && Number.isFinite(hourlyCap) && hourlyCap > 0
    && hourlyUsed >= Math.floor(hourlyCap * 2 / 3);
  const isRateLimited = phase === "rate_limited" && cooldownSecs > 0;
  const btnDisabled = disabled || !!statusErr || !!providerBlocked || !!capReached
                    || phase === "stopping" || phase === "uploading"
                    || isRateLimited;

  let label = "Speak answer";
  let Icon = Mic;
  if (phase === "recording")  { label = `Stop (${elapsed}s)`; Icon = Square; }
  if (phase === "stopping")   { label = "Finalizing…"; Icon = Loader2; }
  if (phase === "uploading")  { label = "Transcribing…"; Icon = Loader2; }
  if (phase === "requesting_perm") { label = "Allow mic…"; Icon = Mic; }
  if (isRateLimited) {
    const mm = String(Math.floor(cooldownSecs / 60)).padStart(2, "0");
    const ss = String(cooldownSecs % 60).padStart(2, "0");
    label = `Voice paused ${mm}:${ss}`;
    Icon = Mic;
  }

  // Cause-specific honest tooltip. No upgrade CTA — voice isn't a
  // purchasable product today.
  let honestTooltip = "";
  if (planExcludesVoice) {
    honestTooltip = "Voice input isn't included on your current plan — please type your answer.";
  } else if (providerMisconfigured) {
    honestTooltip = "Voice input service isn't set up right now — please type your answer.";
  } else if (capReached) {
    honestTooltip = "You've reached today's voice-transcription limit — please type or try again tomorrow.";
  } else if (statusErr) {
    honestTooltip = "Voice input status couldn't be loaded — please type your answer.";
  }

  return (
    <>
      <div className="inline-flex flex-col items-start gap-1">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant={phase === "recording" ? "destructive" : "ghost"}
            onClick={handleTap}
            disabled={btnDisabled}
            aria-label={ariaLabel}
            aria-pressed={phase === "recording"}
            data-testid={fieldTestid}
            title={honestTooltip || label}
            className={
              phase === "recording"
                ? "bg-destructive/90 text-bg-base hover:bg-destructive"
                : "text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
            }
          >
            <Icon className={`h-3.5 w-3.5 mr-2 ${phase === "stopping" || phase === "uploading" ? "animate-spin" : ""}`} aria-hidden="true" />
            {label}
          </Button>
          {phase === "recording" && (
            <>
              <span
                data-testid={`${fieldTestid}-live-indicator`}
                aria-live="polite"
                aria-atomic="true"
                className="text-xs text-destructive flex items-center gap-1"
              >
                <span className="inline-block h-2 w-2 rounded-full bg-destructive animate-pulse" aria-hidden="true" />
                Recording — {elapsed}s / {maxSeconds}s
              </span>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => stop(true)}
                data-testid={`${fieldTestid}-cancel`}
                className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
              >
                <X className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                Cancel
              </Button>
            </>
          )}
        </div>

        {honestTooltip && (
          <p className="text-[11px] text-fg-muted italic max-w-xs"
             data-testid={`${fieldTestid}-honest-note`}>
            {honestTooltip}
          </p>
        )}
        {/* V+ pre-limit indicator: calm, not nagging. */}
        {!isRateLimited && showHourlyIndicator && (
          <p
            data-testid={`${fieldTestid}-hourly-indicator`}
            className="text-[11px] text-fg-muted"
          >
            {hourlyUsed} of {hourlyCap} voice transcriptions used this hour.
          </p>
        )}
        {/* V1 honest countdown state. */}
        {isRateLimited && (
          <div
            role="status" aria-live="polite"
            data-testid={`${fieldTestid}-cooldown`}
            className="mt-1 flex items-start gap-2 text-xs text-fg-secondary max-w-md"
          >
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-accent-warm" aria-hidden="true" />
            <div>
              <p>
                <span className="text-fg-primary font-medium">
                  Voice paused for {String(Math.floor(cooldownSecs / 60)).padStart(2, "0")}
                  :{String(cooldownSecs % 60).padStart(2, "0")}
                </span>{" "}
                — you've used {hourlyCap ?? 30}/{hourlyCap ?? 30} transcriptions
                this hour. Keep typing meanwhile; the button re-enables when
                the timer ends.
              </p>
            </div>
          </div>
        )}
        {phase === "error" && errorMsg && (
          <div
            role="status" aria-live="polite"
            data-testid={`${fieldTestid}-error`}
            className="mt-1 flex items-start gap-2 text-xs text-destructive max-w-md"
          >
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <p>{errorMsg}</p>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => { setErrorMsg(null); setPhase("idle"); handleTap(); }}
                data-testid={`${fieldTestid}-retry`}
                className="mt-1 text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                Try again
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={consentOpen}
               onOpenChange={(v) => { if (!v && !consenting) setConsentOpen(false); }}>
        <DialogContent
          data-testid={`${fieldTestid}-consent-dialog`}
          className="bg-bg-surface border-border-subtle text-fg-primary"
        >
          <DialogHeader>
            <DialogTitle className="font-heading">
              Use voice input?
            </DialogTitle>
            <DialogDescription className="text-fg-secondary space-y-2">
              <span className="block">
                Your microphone recording is sent to our AI transcription
                provider (OpenAI Whisper). We don't keep the recording.
                The transcript is added to this text box — you can edit or
                erase it before submitting. Nothing is submitted
                automatically.
              </span>
              <span className="block text-[11px] italic">
                Working draft consent v{status?.consent_policy?.version || "1.0"}.
                You can review the full text on the Privacy page.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              variant="ghost"
              onClick={() => setConsentOpen(false)}
              disabled={consenting}
              data-testid={`${fieldTestid}-consent-cancel`}
              className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
            >
              No thanks — I'll type
            </Button>
            <Button
              onClick={submitConsent}
              disabled={consenting || !status?.consent_policy?.version}
              data-testid={`${fieldTestid}-consent-agree`}
              className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
            >
              {consenting ? "Saving…" : "I agree — start recording"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


/**
 * Default merger utility for a plain textarea. Appends `transcript`
 * to `current`. Preserves the customer's existing text verbatim.
 * Never overwrites.
 *
 *   const merged = appendTranscript(currentValue, "hello world");
 *
 * If the field already ends with whitespace, we don't add another
 * separator. Otherwise we insert " " to keep the sentence readable.
 */
export function appendTranscript(current, transcript) {
  const cur = (current == null ? "" : String(current));
  const add = (transcript == null ? "" : String(transcript)).trim();
  if (!add) return cur;
  if (!cur) return add;
  const needsSep = !/[\s\n]$/.test(cur);
  return needsSep ? `${cur} ${add}` : `${cur}${add}`;
}


export default VoiceInputButton;
